import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Panel } from "./panel";
import { LossDevelopmentChart } from "./charts/loss-development-chart";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import {
  ALL_INDUSTRIES_LABEL,
  ULTIMATE_AGE,
  useDevelopmentLedger,
  useLossDevelopment,
} from "../../pages/AthenaPricingEngine/lib/api/calibration";
import type { DevelopmentFactor, LossDevelopmentLedgerRow } from "../../pages/AthenaPricingEngine/lib/api/calibration";
import { useIndustries } from "../../pages/AthenaPricingEngine/lib/api/reference";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group";
import { cn } from "../../utils/common/cn";

/** Realized vs Projected. Same mechanism as Segments.tsx's RAV basis toggle
 *  in spirit, but a client-side filter rather than a `?basis=` round-trip:
 *  isProjected/actualUltimateLossPct already tell the frontend which basis
 *  each already-fetched row belongs to, so there is nothing to refetch. */
type LossBasis = "realized" | "projected";

const SCOPE_CAPTION =
  "Built by industry for now. Term materially affects development speed, so industry-by-term is a planned refinement, not the final design.";

/** Sentinel for the ledger's year filter, matching ALL_INDUSTRIES_LABEL's role
 *  next to it. A Radix Select cannot hold an empty-string value, so "no filter"
 *  needs a real one. */
const ALL_YEARS_LABEL = "All Years";

/** Explicit months here, unlike the chart's x-axis (which labels 60 as
 *  "ultimate") -- these are age-to-age transitions, so "48m → 60m" reads
 *  better than "48m → ultimate". */
function ageLabel(age: number) {
  return `${age}m`;
}

function FactorsTable({ factors }: { factors: DevelopmentFactor[] }) {
  return (
    <div className="mt-6 overflow-x-auto border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {factors.map((factor) => (
              <th
                key={`${factor.fromAge}-${factor.toAge}`}
                className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {ageLabel(factor.fromAge)} → {ageLabel(factor.toAge)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {factors.map((factor) => (
              <td
                key={`${factor.fromAge}-${factor.toAge}`}
                className="px-3 py-2.5 text-center font-semibold tabular-nums text-foreground"
              >
                {factor.factor.toFixed(3)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Top-level sort/display columns once Age At Projection moves down a level
 *  (see VintageGroup below) -- everything else about the metrics is unchanged. */
type LedgerSortKey = "vintage" | "industry" | "projectedUltimateLossPct" | "actualUltimateLossPct" | "errorPct";

const LEDGER_COLUMNS: Array<{ key: LedgerSortKey; label: string; align: "left" | "right" }> = [
  { key: "vintage", label: "Vintage", align: "left" },
  { key: "industry", label: "Industry", align: "left" },
  { key: "projectedUltimateLossPct", label: "Projected Loss", align: "right" },
  { key: "actualUltimateLossPct", label: "Actual Loss", align: "right" },
  { key: "errorPct", label: "Error", align: "right" },
];

/** One Vintage's full term structure -- `ultimate` is the 60mo checkpoint
 *  (falling back to the oldest age actually present, for the unexpected case
 *  a vintage has no 60mo row at all), which is what the collapsed row shows:
 *  "Projected/Actual Ultimate" have always meant the 60-month figure
 *  specifically, the same way Active Policy's collapsed office row shows that
 *  office's own (All Industries) scope rather than any one industry's --
 *  both pick the one row that already stands for "the whole thing" rather
 *  than an arbitrary member of the group. `ages` holds every age (12-60,
 *  ultimate included) for the drill-down. */
interface VintageGroup {
  vintage: number;
  industry: string;
  ultimate: LossDevelopmentLedgerRow;
  ages: LossDevelopmentLedgerRow[];
}

function groupByVintage(rows: LossDevelopmentLedgerRow[]): VintageGroup[] {
  const byVintage = new Map<number, LossDevelopmentLedgerRow[]>();
  for (const row of rows) {
    const group = byVintage.get(row.vintage);
    if (group) group.push(row);
    else byVintage.set(row.vintage, [row]);
  }
  return Array.from(byVintage.entries()).map(([vintage, ages]) => {
    const sortedAges = [...ages].sort((a, b) => a.ageAtProjectionMonths - b.ageAtProjectionMonths);
    const ultimate =
      sortedAges.find((row) => row.ageAtProjectionMonths === ULTIMATE_AGE) ??
      sortedAges[sortedAges.length - 1];
    return { vintage, industry: ultimate.industry, ultimate, ages: sortedAges };
  });
}

function AgeBreakdownTable({ ages }: { ages: LossDevelopmentLedgerRow[] }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-border/60">
          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Age At Projection
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Projected
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Actual
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Error
          </th>
        </tr>
      </thead>
      <tbody>
        {ages.map((row) => (
          <tr key={row.ageAtProjectionMonths} className="border-b border-border/40 last:border-b-0">
            <td className="px-3 py-2 tabular-nums text-foreground">
              {ageLabel(row.ageAtProjectionMonths)}
              {row.ageAtProjectionMonths === ULTIMATE_AGE ? (
                <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Ultimate
                </span>
              ) : null}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.projectedUltimateLossPct.toFixed(2)}%
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.actualUltimateLossPct == null ? (
                <span className="text-muted-foreground">Pending</span>
              ) : (
                `${row.actualUltimateLossPct.toFixed(2)}%`
              )}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.errorPct == null ? (
                <span className="text-muted-foreground">Pending</span>
              ) : (
                `${row.errorPct > 0 ? "+" : ""}${row.errorPct.toFixed(2)}%`
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LedgerTable({ rows }: { rows: LossDevelopmentLedgerRow[] }) {
  const [sortKey, setSortKey] = useState<LedgerSortKey>("vintage");
  const [descending, setDescending] = useState(true);
  // Keyed by vintage -- a vintage's own row is unique within one (already
  // industry-filtered) ledger fetch, same uniqueness FairValueLog's batchId
  // key relies on.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const groups = useMemo(() => groupByVintage(rows), [rows]);

  const sorted = useMemo(() => {
    const copy = [...groups];
    copy.sort((a, b) => {
      const av = sortKey === "vintage" || sortKey === "industry" ? a[sortKey] : a.ultimate[sortKey];
      const bv = sortKey === "vintage" || sortKey === "industry" ? b[sortKey] : b.ultimate[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    });
    return descending ? copy.reverse() : copy;
  }, [groups, sortKey, descending]);

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            <th className="w-8 px-2 py-2.5" />
            {LEDGER_COLUMNS.map((column) => (
              <th
                key={column.key}
                className={cn(
                  "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                  column.align === "right" ? "text-right" : "text-left",
                )}
              >
                <button
                  type="button"
                  className="uppercase tracking-wider hover:text-foreground"
                  onClick={() => {
                    if (sortKey === column.key) setDescending((prev) => !prev);
                    else {
                      setSortKey(column.key);
                      setDescending(true);
                    }
                  }}
                >
                  {column.label}
                  {sortKey === column.key ? (descending ? " ↓" : " ↑") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((group) => (
            <Fragment key={group.vintage}>
              <tr className="border-b border-border/60 last:border-b-0">
                <td className="px-2 py-2.5">
                  <button
                    type="button"
                    className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
                    aria-label={expanded[group.vintage] ? "Collapse term structure" : "Expand term structure"}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [group.vintage]: !e[group.vintage] }))
                    }
                  >
                    {expanded[group.vintage] ? (
                      <ChevronDown className="size-3" />
                    ) : (
                      <ChevronRight className="size-3" />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2.5 font-medium tabular-nums text-foreground">
                  {group.vintage}
                </td>
                <td className="px-3 py-2.5">{group.industry}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.projectedUltimateLossPct.toFixed(2)}%
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.actualUltimateLossPct == null ? (
                    <span className="text-muted-foreground">Pending</span>
                  ) : (
                    `${group.ultimate.actualUltimateLossPct.toFixed(2)}%`
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.errorPct == null ? (
                    <span className="text-muted-foreground">Pending</span>
                  ) : (
                    `${group.ultimate.errorPct > 0 ? "+" : ""}${group.ultimate.errorPct.toFixed(2)}%`
                  )}
                </td>
              </tr>
              {expanded[group.vintage] ? (
                <tr className="border-b border-border/60 last:border-b-0 bg-secondary/20">
                  <td />
                  <td colSpan={LEDGER_COLUMNS.length} className="px-3 pb-3 pt-1">
                    <AgeBreakdownTable ages={group.ages} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Multi-select year filter, mirroring MDM's Observability page: plain
 *  buttons, XOR membership, and an empty selection meaning "all years"
 *  rather than "no years". */
function VintageToggle({
  vintages,
  selected,
  onToggle,
}: {
  vintages: number[];
  selected: number[];
  onToggle: (vintage: number) => void;
}) {
  const active = selected.length ? selected : vintages;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Funded Year
      </span>
      {vintages.map((vintage) => (
        <button
          key={vintage}
          type="button"
          onClick={() => onToggle(vintage)}
          aria-pressed={active.includes(vintage)}
          className={cn(
            "border px-2 py-1 text-xs font-medium tabular-nums transition-colors",
            active.includes(vintage)
              ? "border-primary bg-primary/15 text-foreground"
              : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          {vintage}
        </button>
      ))}
    </div>
  );
}

/**
 * Multi-select Industry filter -- an empty selection means "All Industry"
 * (the precomputed portfolio row), same sentinel-by-omission convention as
 * every other multi-value filter here (e.g. VintageToggle's own empty-means-
 * all). A picked subset is summed by financed amount server-side and derived
 * from the sums once -- the same methodology the real ALL_INDUSTRIES row
 * itself uses -- rather than an average of already-computed per-division
 * rates; see risk-pricing-engine/app/repository/calibration.py's
 * `_industry_filter`/`get_loss_development` for why.
 *
 * A dropdown-with-checkboxes rather than a row of toggle buttons (the
 * VintageToggle pattern): 12 industries would crowd the header far more than
 * 6 funded years already do.
 */
function IndustryMultiSelect({
  industries,
  selected,
  onChange,
}: {
  industries: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const label =
    selected.length === 0
      ? ALL_INDUSTRIES_LABEL
      : selected.length === 1
        ? selected[0]!
        : `${selected.length} Industries`;

  function toggle(industry: string) {
    onChange(
      selected.includes(industry)
        ? selected.filter((i) => i !== industry)
        : [...selected, industry],
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label="Industry"
          className="h-9 w-[220px] justify-between gap-2 text-sm font-normal"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[320px] w-[220px] overflow-y-auto">
        <DropdownMenuCheckboxItem
          checked={selected.length === 0}
          // Keeps the menu open across multiple toggles -- Radix closes a
          // checkbox item by default on select, which would defeat picking
          // more than one industry per open.
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={() => onChange([])}
        >
          {ALL_INDUSTRIES_LABEL}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {industries.map((industry) => (
          <DropdownMenuCheckboxItem
            key={industry}
            checked={selected.includes(industry)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(industry)}
          >
            {industry}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function LossDevelopmentSection() {
  const industriesQuery = useIndustries();
  const industries = industriesQuery.data ?? [];
  // One piece of state drives both panels' selects, so changing either moves
  // both -- same shared-control pattern Discipline uses across its panels.
  // Empty = "All Industry"; one or more real names = a multi-select, summed
  // server-side rather than averaged -- see IndustryMultiSelect's own comment.
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedVintages, setSelectedVintages] = useState<number[]>([]);
  // Scoped to the ledger ONLY. The chart above has its own multi-select
  // VintageToggle, and the two answer different questions -- the toggle picks
  // which curves to draw, this narrows a log of runs to one funded year.
  const [ledgerYear, setLedgerYear] = useState<string>(ALL_YEARS_LABEL);
  // Shared by the chart, its Funded Year toggle, and the ledger -- one control,
  // not three bound together. Defaults to Projected so nothing on this page
  // changes appearance until someone touches it.
  const [basis, setBasis] = useState<LossBasis>("projected");

  // `basis` now drives a real refetch, not just a client-side re-filter:
  // Realized asks the backend for every fully-matured vintage with no year
  // floor (2015+, confirmed live), which is a disjoint set from Projected's
  // 2021+ window for most industries -- not a subset of one fixed fetch
  // anymore. See risk-pricing-engine/app/repository/calibration.py:_vintage_filter.
  const developmentQuery = useLossDevelopment(selectedIndustries, basis);
  const ledgerQuery = useDevelopmentLedger(selectedIndustries, basis);

  const points = developmentQuery.data?.points ?? [];
  // Named "ever" from when one fetch covered both bases and this stabilised
  // colour across the basis toggle too -- that no longer applies (Realized
  // and Projected are now disjoint vintage sets, so there is nothing to keep
  // a shared colour between). What it still does, and is still needed for:
  // stabilising colour across the Funded Year multi-select below, which
  // narrows `points` within ONE basis's fetch.
  const allVintagesEver = useMemo(
    () => Array.from(new Set(points.map((p) => p.vintage))).sort((a, b) => a - b),
    [points],
  );
  // Realized keeps only ages where consider_XXmo was true (isProjected false) --
  // see loss-development-chart.tsx's showProjected prop for why the always-drawn
  // backtest line needs a separate switch on top of this.
  const basisPoints = useMemo(
    () => (basis === "realized" ? points.filter((p) => !p.isProjected) : points),
    [points, basis],
  );
  // Funded Year's own buttons come off the BASIS-filtered set: under Realized, a
  // vintage with zero realized ages (2026, per the module docstring) has nothing
  // to show and drops out rather than offering a button that renders an empty
  // chart.
  const vintages = useMemo(
    () => Array.from(new Set(basisPoints.map((p) => p.vintage))).sort((a, b) => a - b),
    [basisPoints],
  );
  // Derived, not reset in an effect -- same reasoning as activeLedgerYear below.
  // Realized and Projected fetch disjoint vintage sets (2015-2020 vs 2021-2026
  // for an industry like Services), so a selection made under one basis
  // almost never exists under the other; using selectedVintages directly
  // here would filter to nothing and show a blank chart instead of falling
  // back to "show everything available".
  const activeSelectedVintages = selectedVintages.filter((v) => vintages.includes(v));
  const visiblePoints = useMemo(
    () =>
      activeSelectedVintages.length
        ? basisPoints.filter((p) => activeSelectedVintages.includes(p.vintage))
        : basisPoints,
    [basisPoints, activeSelectedVintages],
  );

  // Realized excludes any row not yet validated (errorPct null, rendered as
  // "Pending" in the Error column) -- actualUltimateLossPct itself is now
  // populated from chart_rate at every age regardless of maturity, so it's
  // errorPct, not actualUltimateLossPct, that carries the ledger's own
  // consider_XXmo signal.
  const basisLedgerRows = useMemo(() => {
    const rows = ledgerQuery.data ?? [];
    return basis === "realized" ? rows.filter((row) => row.errorPct != null) : rows;
  }, [ledgerQuery.data, basis]);

  // Off the LEDGER's own (already basis-filtered) rows, not the chart's points:
  // the two come from different queries and need not carry the same years, and
  // offering a year the ledger has no run for would filter to an empty table.
  const ledgerYears = useMemo(
    () => Array.from(new Set(basisLedgerRows.map((row) => row.vintage))).sort((a, b) => b - a),
    [basisLedgerRows],
  );

  // Changing Industry (or the basis) reloads/narrows the ledger, and the result
  // may carry no runs for the selected year. Derived rather than reset in an
  // effect: a stale year would blank the Select's trigger and empty the table
  // with nothing on screen explaining why, and an effect would show that state
  // for one frame first.
  const activeLedgerYear = ledgerYears.some((year) => String(year) === ledgerYear)
    ? ledgerYear
    : ALL_YEARS_LABEL;

  const ledgerRows = useMemo(() => {
    if (activeLedgerYear === ALL_YEARS_LABEL) return basisLedgerRows;
    return basisLedgerRows.filter((row) => String(row.vintage) === activeLedgerYear);
  }, [basisLedgerRows, activeLedgerYear]);

  function toggleVintage(vintage: number) {
    setSelectedVintages((previous) =>
      previous.includes(vintage)
        ? previous.filter((v) => v !== vintage)
        : [...previous, vintage],
    );
  }

  const industrySelect = (
    <IndustryMultiSelect
      industries={industries}
      selected={selectedIndustries}
      onChange={setSelectedIndustries}
    />
  );

  // One shared toggle, rendered once per panel so it sits with that panel's own
  // filters (Industry on Loss Development; Funded Year + Industry on the
  // ledger) rather than as a third, disconnected control above both. Both
  // instances read/write the same `basis` state, so flipping either one moves
  // both panels together -- there is exactly one basis, just two places to
  // reach it.
  const basisToggle = (
    <ToggleGroup
      type="single"
      value={basis}
      onValueChange={(value) => value && setBasis(value as LossBasis)}
      className="border border-border bg-secondary"
      aria-label="Basis"
    >
      <ToggleGroupItem value="realized" className="h-8 px-3 text-xs">
        Realized
      </ToggleGroupItem>
      <ToggleGroupItem value="projected" className="h-8 px-3 text-xs">
        Projected
      </ToggleGroupItem>
    </ToggleGroup>
  );

  // Sits BEFORE the Industry select, and only on the ledger -- `industrySelect`
  // is shared with the Loss Development panel above, so anything folded into it
  // would appear there too.
  const ledgerControls = (
    <div className="flex flex-wrap items-center gap-2">
      {basisToggle}
      <Select value={activeLedgerYear} onValueChange={setLedgerYear}>
        <SelectTrigger className="w-[130px]" aria-label="Funded Year">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_YEARS_LABEL}>{ALL_YEARS_LABEL}</SelectItem>
          {ledgerYears.map((year) => (
            <SelectItem key={year} value={String(year)}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {industrySelect}
    </div>
  );

  return (
    <>
      <Panel
        className="mt-6"
        title="Loss Development"
        description={
          basis === "realized"
            ? "Cumulative loss by vintage against age, realized ages only."
            : "Cumulative loss by vintage against age, actual solid and projected dotted."
        }
        right={
          <div className="flex flex-wrap items-center gap-2">
            {basisToggle}
            {industrySelect}
          </div>
        }
      >
        <p className="-mt-2 mb-4 text-xs text-muted-foreground">{SCOPE_CAPTION}</p>

        {developmentQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load loss development for{" "}
            {selectedIndustries.length ? selectedIndustries.join(", ") : ALL_INDUSTRIES_LABEL}.
          </div>
        ) : developmentQuery.isLoading || !developmentQuery.data ? (
          <Skeleton className="h-[340px] w-full" />
        ) : (
          <>
            <VintageToggle
              vintages={vintages}
              selected={activeSelectedVintages}
              onToggle={toggleVintage}
            />
            <LossDevelopmentChart
              points={visiblePoints}
              allVintages={allVintagesEver}
              showProjected={basis === "projected"}
            />
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Age-to-Age Development Factors
            </p>
            <FactorsTable factors={developmentQuery.data.factors} />
            <p className="mt-2 text-xs text-muted-foreground">
              Projected development multiples for the most recent funded year.
            </p>
          </>
        )}
      </Panel>

      <Panel
        className="mt-6"
        title="Validation Ledger"
        description={
          basis === "realized"
            ? "Every projection run ever made, realized ages only -- rows still pending an actual are hidden, not deleted."
            : "Every projection run ever made. Rows are appended, never replaced or deleted."
        }
        right={ledgerControls}
      >
        {ledgerQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load the validation ledger.
          </div>
        ) : ledgerQuery.isLoading || !ledgerQuery.data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <LedgerTable rows={ledgerRows} />
        )}
      </Panel>
    </>
  );
}
